/**
 * Inferred intent of outreach — rule-based classification on
 * engagement names + content. Buckets every engagement into one of:
 * PrEP, Medi-Cal renewal/enrollment, ECM, CHW, SDOH, Vaccination,
 * Reminder, Survey, Benefit access, Eligibility, Onboarding,
 * Educational, Generic enrollment, or Other / unclassified.
 */

import React, { useMemo } from 'react';
import { useIntentBreakdown } from '@/hooks/ml';

const COLOR_FOR_INTENT: Record<string, string> = {
  'PrEP outreach': 'bg-rose-500',
  'Medi-Cal renewal': 'bg-amber-500',
  'Medi-Cal enrollment': 'bg-orange-500',
  'ECM enrollment': 'bg-indigo-500',
  'CHW outreach': 'bg-sky-500',
  'SDOH screening': 'bg-fuchsia-500',
  'Vaccination outreach': 'bg-emerald-500',
  'Appointment reminder': 'bg-blue-500',
  'Survey / feedback': 'bg-purple-500',
  'Benefit access': 'bg-yellow-500',
  'Eligibility check': 'bg-lime-500',
  Onboarding: 'bg-cyan-500',
  Educational: 'bg-teal-500',
  'Generic enrollment': 'bg-violet-500',
  'Other / unclassified': 'bg-gray-400',
};

export const InferredIntentCard: React.FC = () => {
  const { data, isLoading } = useIntentBreakdown();

  // Aggregate by intent (sum across language + cbo)
  const intents = useMemo(() => {
    const rows = data?.rows ?? [];
    const map = new Map<
      string,
      { intent: string; engagements: number; sms_sent: number; languages: Set<string> }
    >();
    for (const r of rows) {
      const cur = map.get(r.inferred_intent) ?? {
        intent: r.inferred_intent,
        engagements: 0,
        sms_sent: 0,
        languages: new Set<string>(),
      };
      cur.engagements += r.engagement_count;
      cur.sms_sent += r.total_sms_sent;
      cur.languages.add(r.message_language);
      map.set(r.inferred_intent, cur);
    }
    return Array.from(map.values()).sort((a, b) => b.engagements - a.engagements);
  }, [data]);

  const total = intents.reduce((a, r) => a + r.engagements, 0);
  const totalSms = intents.reduce((a, r) => a + r.sms_sent, 0);
  const classifiedShare = total > 0
    ? (1 - (intents.find((r) => r.intent === 'Other / unclassified')?.engagements ?? 0) / total) * 100
    : 0;

  return (
    <div className="space-y-3">
      <div className="text-xs text-gray-500">
        Engagements bucketed by inferred intent based on engagement title patterns
        (regex over names like &quot;ECM&quot;, &quot;Medi-Cal&quot;, &quot;PrEP&quot;, etc.).
        {' '}
        <span className="font-medium text-gray-700">{classifiedShare.toFixed(1)}%</span> of
        engagements have a recognizable intent; the rest are coded shorthand the
        classifier can&apos;t parse yet.
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : intents.length === 0 ? (
        <div className="text-sm text-gray-500">No engagements to classify.</div>
      ) : (
        <>
          {/* Stacked bar of intent shares */}
          <div className="flex h-3 w-full overflow-hidden rounded-md bg-gray-100" title="Intent distribution">
            {intents.map((r) => {
              const w = total > 0 ? (r.engagements / total) * 100 : 0;
              if (w < 0.5) return null;
              return (
                <div
                  key={r.intent}
                  className={COLOR_FOR_INTENT[r.intent] ?? 'bg-gray-300'}
                  style={{ width: `${w}%` }}
                  title={`${r.intent}: ${r.engagements.toLocaleString()} engagements (${w.toFixed(1)}%)`}
                />
              );
            })}
          </div>

          {/* Detail table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                <tr className="border-b border-gray-200">
                  <th className="py-2 pr-3">Intent</th>
                  <th className="py-2 pr-3 text-right">Engagements</th>
                  <th className="py-2 pr-3 text-right">Total SMS sent</th>
                  <th className="py-2 pr-3 text-right">Languages</th>
                  <th className="py-2 pr-3 text-right">Share</th>
                </tr>
              </thead>
              <tbody>
                {intents.map((r) => {
                  const pct = total > 0 ? (r.engagements / total) * 100 : 0;
                  return (
                    <tr key={r.intent} className="border-b border-gray-100 last:border-0">
                      <td className="py-1.5 pr-3 font-medium text-gray-900">
                        <span
                          className={`inline-block h-2 w-2 rounded-full mr-2 ${COLOR_FOR_INTENT[r.intent] ?? 'bg-gray-300'}`}
                        />
                        {r.intent}
                      </td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{r.engagements.toLocaleString()}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{r.sms_sent.toLocaleString()}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{r.languages.size}</td>
                      <td className="py-1.5 pr-3 text-right tabular-nums">{pct.toFixed(1)}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="text-xs text-gray-500">
            Total: {total.toLocaleString()} engagements, {totalSms.toLocaleString()} SMS sent.
          </div>
        </>
      )}
    </div>
  );
};
